/**
 * Admin Revenue Dashboard - P3 Feature
 * 
 * Ledger-driven revenue analytics:
 * - Top KPI tiles (Gross, MRR, Active Subs, etc.)
 * - Daily revenue chart
 * - Product mix breakdown
 * - Recent transactions table
 */

import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { ADMIN_ROUTES } from "../../config/adminRoutes";
import { 
  ArrowLeft, RefreshCw, TrendingUp, DollarSign, Users, 
  CreditCard, ArrowUpRight, ArrowDownRight, Package, Clock
} from "lucide-react";
import InfoIconButton from "../../components/ui/InfoIconButton";

const API = window.location.origin;

// Format cents to dollars
function fmt$(cents) {
  if (typeof cents !== "number") return "$0.00";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Format date
function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return "—";
  }
}

// KPI Tile component
function KPITile({ label, value, subValue, icon: Icon, trend, color = "slate", metricKey }) {
  const colorClasses = {
    slate: "border-slate-700 bg-slate-800/50",
    emerald: "border-emerald-500/30 bg-emerald-500/10",
    purple: "border-purple-500/30 bg-purple-500/10",
    amber: "border-amber-500/30 bg-amber-500/10",
    rose: "border-rose-500/30 bg-rose-500/10",
  };
  const iconColors = {
    slate: "text-slate-400",
    emerald: "text-emerald-400",
    purple: "text-purple-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  };

  return (
    <div className={`rounded-2xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs text-slate-500">{label}</span>
            {metricKey && <InfoIconButton metricKey={metricKey} size="xs" />}
          </div>
          <div className="text-2xl font-bold text-slate-100">{value}</div>
          {subValue && <div className="text-xs text-slate-400 mt-1">{subValue}</div>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span>{Math.abs(trend)}% vs last period</span>
        </div>
      )}
    </div>
  );
}

// Mini bar chart for daily revenue
function MiniChart({ data, height = 100 }) {
  if (!data || data.length === 0) return null;
  
  const maxVal = Math.max(...data.map(d => d.gross), 1);
  const barWidth = Math.max(4, Math.floor(100 / data.length) - 1);
  
  return (
    <div className="flex items-end gap-1 justify-center" style={{ height }}>
      {data.slice(-30).map((d, i) => {
        const barHeight = Math.max(2, (d.gross / maxVal) * height);
        return (
          <div
            key={d.date}
            className="bg-purple-500/60 hover:bg-purple-400 rounded-t transition-all cursor-pointer"
            style={{ width: barWidth, height: barHeight }}
            title={`${d.date}: ${fmt$(d.gross)}`}
          />
        );
      })}
    </div>
  );
}

// Product breakdown table
function ProductTable({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return <div className="text-sm text-slate-500 text-center py-4">No product data</div>;
  }
  
  const sorted = Object.entries(data).sort((a, b) => b[1].gross - a[1].gross);
  
  return (
    <div className="space-y-2">
      {sorted.map(([product, stats]) => (
        <div key={product} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-300">{product}</span>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-slate-200">{fmt$(stats.gross)}</div>
            <div className="text-xs text-slate-500">{stats.count} sales</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Transaction row
function TransactionRow({ tx, onUserClick }) {
  const typeColors = {
    subscription_grant: "text-emerald-400",
    subscription_renewal: "text-emerald-400",
    consumable_grant: "text-purple-400",
    lovecoins_grant: "text-amber-400",
    refund_reversal: "text-rose-400",
    dispute_hold: "text-rose-400",
    admin_override: "text-cyan-400",
  };
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">
          <div className={`text-xs font-mono ${typeColors[tx.ledger_type] || 'text-slate-400'}`}>
            {tx.ledger_type?.replace(/_/g, ' ')}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-sm text-slate-300 truncate">
            {tx.product || tx.sku}
          </div>
          <div className="text-xs text-slate-500">
            <button 
              onClick={() => onUserClick(tx.user_id)}
              className="hover:text-purple-400 transition"
            >
              {tx.user_email || tx.user_id?.slice(0, 8)}
            </button>
            <span className="mx-1">•</span>
            <span>{fmtDate(tx.created_at)}</span>
          </div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className={`text-sm font-medium ${tx.amount_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
          {tx.amount_cents < 0 ? '-' : '+'}{fmt$(Math.abs(tx.amount_cents))}
        </div>
      </div>
    </div>
  );
}

export default function AdminRevenuePage() {
  const { token } = useContext(AuthContext);
  const nav = useNavigate();
  
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [products, setProducts] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function loadAll() {
    setBusy(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sumRes, dailyRes, prodRes, txRes] = await Promise.all([
        fetch(`${API}/api/admin/revenue/summary`, { headers }),
        fetch(`${API}/api/admin/revenue/daily?days=30`, { headers }),
        fetch(`${API}/api/admin/revenue/products?days=30`, { headers }),
        fetch(`${API}/api/admin/revenue/transactions?limit=20`, { headers }),
      ]);
      
      if (!sumRes.ok) throw new Error("Failed to load summary");
      if (!dailyRes.ok) throw new Error("Failed to load daily data");
      if (!prodRes.ok) throw new Error("Failed to load product data");
      if (!txRes.ok) throw new Error("Failed to load transactions");
      
      const [sumData, dailyData, prodData, txData] = await Promise.all([
        sumRes.json(),
        dailyRes.json(),
        prodRes.json(),
        txRes.json(),
      ]);
      
      setSummary(sumData);
      setDaily(dailyData.days || []);
      setProducts(prodData.by_product || {});
      setTransactions(txData.transactions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadAll();
    
  }, []);

  const handleUserClick = (userId) => {
    if (userId) nav(ADMIN_ROUTES.USER_360(userId));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => nav(ADMIN_ROUTES.DASHBOARD)}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
              Revenue Dashboard
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Ledger-driven analytics • All data from entitlement_ledger
            </p>
          </div>

          <button
            onClick={loadAll}
            disabled={busy}
            className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KPITile
            label="Gross (30d)"
            value={fmt$(summary?.gross_30d)}
            subValue={`MTD: ${fmt$(summary?.gross_mtd)}`}
            icon={DollarSign}
            color="emerald"
            metricKey="gross_revenue"
          />
          <KPITile
            label="Net (30d)"
            value={fmt$(summary?.net_30d)}
            subValue={`Refunds: ${fmt$(summary?.refunds_30d)}`}
            icon={TrendingUp}
            color="purple"
            metricKey="net_revenue"
          />
          <KPITile
            label="MRR Estimate"
            value={fmt$(summary?.mrr_estimate)}
            subValue={`${summary?.active_subs?.total || 0} active subs`}
            icon={CreditCard}
            color="amber"
            metricKey="mrr"
          />
          <KPITile
            label="Active Subs"
            value={summary?.active_subs?.total || 0}
            subValue={`E: ${summary?.active_subs?.elite || 0} • E+: ${summary?.active_subs?.elite_plus || 0} • W: ${summary?.active_subs?.wingman_pro || 0}`}
            icon={Users}
            color="slate"
            metricKey="active_subs"
          />
        </div>

        {/* Second row KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KPITile
            label="One-Time Sales (30d)"
            value={fmt$(summary?.one_time_30d)}
            icon={Package}
            color="slate"
            metricKey="one_time_sales"
          />
          <KPITile
            label="Transactions (30d)"
            value={summary?.tx_count_30d || 0}
            subValue={`MTD: ${summary?.tx_count_mtd || 0}`}
            icon={Clock}
            color="slate"
          />
          <KPITile
            label="All-Time Gross"
            value={fmt$(summary?.gross_all_time)}
            icon={DollarSign}
            color="slate"
            metricKey="gross_revenue"
          />
          <KPITile
            label="Refunds (30d)"
            value={fmt$(summary?.refunds_30d)}
            subValue={`${summary?.refunds_30d_count || 0} refunds`}
            icon={ArrowDownRight}
            color="rose"
            metricKey="refunds"
          />
        </div>

        {/* Charts and Tables */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Daily Revenue Chart */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Daily Revenue (30d)</h3>
            <MiniChart data={daily} height={120} />
            <div className="mt-3 text-xs text-slate-500 text-center">
              Hover bars for daily totals
            </div>
          </div>

          {/* Product Mix */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Product Mix (30d)</h3>
            <ProductTable data={products} />
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-300">Recent Transactions</h3>
            <span className="text-xs text-slate-500">{transactions.length} shown</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {transactions.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">No transactions yet</div>
            ) : (
              transactions.map((tx, i) => (
                <TransactionRow key={tx.id || i} tx={tx} onUserClick={handleUserClick} />
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-xs text-slate-500 text-center">
          Data sourced from <span className="font-mono">entitlement_ledger</span> • 
          Last updated: {summary?.as_of ? new Date(summary.as_of).toLocaleString() : '—'}
        </div>
      </div>
    </div>
  );
}
